import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { LogoutRounded, PersonOutlineRounded } from '@mui/icons-material';
import Hoverable from './Hoverable';
import { useTheme } from '../context/ThemeContext';
import { getNotificationsCount } from '../store/actions/dataActions';
import { logout } from '../store/actions/authActions';
import { getRequest } from '../../core/network';
import endpoints from '../../constants/endPoints.json';
import { iconBtn } from './styles';
import GlassSelect from './GlassSelect';
import { cityFromAddress } from './OrderTableCells';

function TopBar({ isMobile = false, onOpenNav, user, token, getNotificationsCount, notificationsCount, logout }) {
  const { currentTheme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  useEffect(() => {
    getNotificationsCount({});
    const interval = setInterval(() => getNotificationsCount({}), 900000);
    return () => clearInterval(interval);
  }, [getNotificationsCount]);

  useEffect(() => {
    const q = query.trim();
    if (!q || !token) {
      setResults([]);
      return;
    }
    setSearching(true);
    const handle = setTimeout(() => {
      getRequest({ search: q, limit: 6, page: 1 }, undefined, token, endpoints.partner.orders)
        .then((res) => {
          const docs = res.data?.docs || res.data?.orders?.docs || [];
          setResults(Array.isArray(docs) ? docs.slice(0, 6) : []);
        })
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 280);
    return () => clearTimeout(handle);
  }, [query, token]);

  const goToOrder = (id) => {
    setOpen(false);
    setQuery('');
    navigate(`/orders/${id}`);
  };

  const name = user?.name || user?.vendor?.name || t('navigation.partner');
  const initial = (name || 'P').charAt(0).toUpperCase();

  const orderLabel = (ord) =>
    ord.orderNumber || ord.name || ord.shopifyOrderNumber || `#${String(ord._id || '').slice(-6)}`;
  const customerName = (ord) =>
    ord.customer?.name || ord.shipping_address?.name || ord.customerName || '—';
  const city = (ord) =>
    cityFromAddress(ord.fulfillment?.shippingAddress || ord.orderFulfillment?.shippingAddress || ord.shipping_address)
    || ord.customer?.city
    || ord.city
    || '';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 18 }}>
      {isMobile && (
        <Hoverable as="button" onClick={onOpenNav} base={iconBtn} hover={{ background: 'var(--s08)' }} aria-label="Open menu">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </Hoverable>
      )}

      <div style={{ position: 'relative', ...(isMobile ? { flex: 1, minWidth: 0 } : { width: 'min(400px, 100%)' }) }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '13px 20px',
            borderRadius: 999,
            background: 'linear-gradient(120deg, var(--s09), var(--s03))',
            border: '1px solid var(--s10)',
            boxShadow: 'inset 0 1px 0 var(--s10), 0 12px 30px var(--sh35)',
            color: 'var(--mut)',
          }}
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.2-3.2" />
          </svg>
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 140)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setOpen(false);
                e.currentTarget.blur();
              }
              if (e.key === 'Enter' && results[0]) goToOrder(results[0]._id);
            }}
            placeholder={t('chrome.searchOrders')}
            style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', color: 'var(--tx)', fontSize: 14, letterSpacing: '0.01em', fontFamily: 'inherit' }}
          />
        </div>

        {open && query.trim() && (
          <div
            onMouseDown={(e) => e.preventDefault()}
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              insetInlineStart: 0,
              insetInlineEnd: 0,
              zIndex: 60,
              padding: 8,
              borderRadius: 18,
              background: 'var(--side-bg)',
              border: '1px solid var(--s10)',
              boxShadow: '0 22px 50px var(--sh50)',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <div style={{ fontSize: 10, color: 'var(--mut)', letterSpacing: '0.08em', padding: '6px 12px 8px' }}>
              {t('navigation.orders').toUpperCase()}
            </div>
            {searching && results.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--mut)', padding: '10px 12px' }}>{t('common.loading')}</div>
            ) : results.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--mut)', padding: '10px 12px' }}>
                {t('chrome.noOrdersMatch', { q: query.trim() })}
              </div>
            ) : (
              results.map((ord) => (
                <Hoverable
                  key={ord._id}
                  onClick={() => goToOrder(ord._id)}
                  base={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 12px', borderRadius: 12, cursor: 'pointer' }}
                  hover={{ background: 'var(--s07)' }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      flexShrink: 0,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      background: 'linear-gradient(160deg, var(--s10), var(--s03))',
                      border: '1px solid var(--s10)',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--red-tx)',
                    }}
                  >
                    {String(customerName(ord)).charAt(0).toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--tx)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {customerName(ord)}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--mut)', marginTop: 2 }}>
                      {orderLabel(ord)} {city(ord) ? `· ${city(ord)}` : ''}
                    </div>
                  </div>
                  <div
                    style={{
                      flexShrink: 0,
                      padding: '4px 10px',
                      borderRadius: 999,
                      fontSize: 10.5,
                      fontWeight: 600,
                      color: 'var(--red-tx)',
                      background: 'rgba(125,1,12,0.30)',
                      border: '1px solid rgba(190,50,62,0.40)',
                    }}
                  >
                    {ord.status || '—'}
                  </div>
                </Hoverable>
              ))
            )}
          </div>
        )}
      </div>

      <div style={{ marginInlineStart: 'auto', display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 14, flexShrink: 0 }}>
        <GlassSelect
          compact
          value={i18n.language?.startsWith('ar') ? 'ar' : 'en'}
          onChange={(lang) => i18n.changeLanguage(lang)}
          ariaLabel={t('language.label', { defaultValue: 'Language' })}
          options={[
            { value: 'en', label: 'EN' },
            { value: 'ar', label: 'AR' },
          ]}
        />

        <Hoverable as="button" onClick={toggleTheme} base={iconBtn} hover={{ background: 'var(--s08)' }} aria-label="Toggle theme">
          {currentTheme === 'light' ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
            </svg>
          )}
        </Hoverable>

        <Hoverable
          as="button"
          onClick={() => navigate('/notifications')}
          base={{ ...iconBtn, position: 'relative' }}
          hover={{ background: 'var(--s08)' }}
          aria-label={t('navigation.notifications')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 9a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6" />
            <path d="M10.5 19a1.8 1.8 0 0 0 3 0" />
          </svg>
          {Number(notificationsCount) > 0 && (
            <div
              style={{
                position: 'absolute',
                top: -5,
                insetInlineEnd: -5,
                minWidth: 18,
                height: 18,
                borderRadius: 999,
                background: 'linear-gradient(160deg, #9c0410, #7D010C)',
                border: '1.5px solid var(--badge-ring)',
                display: 'grid',
                placeItems: 'center',
                fontSize: 10,
                fontWeight: 700,
                color: '#ffeaea',
                padding: '0 4px',
              }}
            >
              {notificationsCount > 99 ? '99+' : notificationsCount}
            </div>
          )}
        </Hoverable>

        <div style={{ position: 'relative' }}>
          <div
            onClick={() => setAccountOpen((v) => !v)}
            onBlur={() => setTimeout(() => setAccountOpen(false), 160)}
            tabIndex={0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 11,
              padding: isMobile ? 4 : '6px 16px 6px 6px',
              borderRadius: 999,
              background: 'linear-gradient(160deg, var(--s09), var(--s02))',
              border: '1px solid var(--s10)',
              cursor: 'pointer',
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: '50%',
                background: 'linear-gradient(160deg, #9c0410, #4a0107)',
                display: 'grid',
                placeItems: 'center',
                fontSize: 14,
                fontWeight: 600,
                color: '#ffeaea',
              }}
            >
              {initial}
            </div>
            {!isMobile && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{name}</div>
                <div style={{ fontSize: 11, color: 'var(--mut)' }}>{t('navigation.partner')}</div>
              </div>
            )}
          </div>

          {accountOpen && (
            <div
              onMouseDown={(e) => e.preventDefault()}
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                insetInlineEnd: 0,
                zIndex: 70,
                minWidth: 200,
                padding: 8,
                borderRadius: 16,
                background: 'var(--side-bg)',
                border: '1px solid var(--s10)',
                boxShadow: '0 22px 50px var(--sh50)',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Hoverable
                onClick={() => {
                  setAccountOpen(false);
                  navigate('/settings?tab=user');
                }}
                base={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  borderRadius: 12,
                  cursor: 'pointer',
                  color: 'var(--tx2)',
                  fontSize: 13.5,
                }}
                hover={{ background: 'var(--s07)', color: 'var(--tx)' }}
              >
                <PersonOutlineRounded sx={{ fontSize: 18 }} />
                {t('navigation.settings')}
              </Hoverable>
              <Hoverable
                onClick={() => {
                  setAccountOpen(false);
                  logout();
                }}
                base={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  borderRadius: 12,
                  cursor: 'pointer',
                  color: 'var(--red-tx2)',
                  fontSize: 13.5,
                }}
                hover={{ background: 'rgba(125,1,12,0.18)' }}
              >
                <LogoutRounded sx={{ fontSize: 18 }} />
                {t('navigation.logout')}
              </Hoverable>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  token: state?.auth?.token,
  notificationsCount: state?.records?.notificationsCount,
});

export default connect(mapStateToProps, { getNotificationsCount, logout })(TopBar);
